import React, { useState } from 'react';
import {
  Badge,
  Button,
  Group,
  Paper,
  CloseButton,
  Select,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { useTranslation } from 'react-i18next';
import { useMyPermissions } from '../../hooks/usePermission';
import { useUserStore } from '../../store/userStore';
import { useUsersDirectory } from '../../hooks/useUserApi';
import {
  AssignmentRow,
  useGrantRole,
  useRevokeRole,
  useRoleAssignments,
  useRoles,
  useRolesCatalog,
} from '../../hooks/useRolesApi';
import { getErrorMessage } from '../../utils/api';
import { formatDateShort } from '../../utils/date';
import { showToast } from '../../utils/toast';
import { beyondMine, grantsOf } from './shared';

export const PeopleTab: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { data: catalog } = useRolesCatalog();
  const { data: roles = [] } = useRoles();
  const { data: assignments } = useRoleAssignments();
  const { data: people = [] } = useUsersDirectory();
  const { data: perms } = useMyPermissions();
  const myId = useUserStore((st) => st.user?._id);
  const grant = useGrantRole();
  const revoke = useRevokeRole();
  const [user, setUser] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const locale = i18n.language?.startsWith('en') ? 'en-US' : 'ru-RU';
  const onError = (e: unknown) =>
    showToast({ title: t('common.error'), message: getErrorMessage(e), color: 'red' });

  const ownerId = assignments?.ownerId;
  const rows = assignments?.items ?? [];
  const grantsOfRole = (id: string | null) => {
    const r = roles.find((x) => x._id === id);
    return r && catalog ? grantsOf(r, catalog) : [];
  };
  // Роль, где есть права сверх моих, ни выдать, ни забрать нельзя — сервер ответит 403.
  const isBeyond = (id: string | null, isSelf = false) => {
    const r = roles.find((x) => x._id === id);
    return !!r && !!catalog && beyondMine(r, catalog, perms, isSelf).length > 0;
  };

  // Себе — ещё и ничего, что откроет своё: список зависит от выбранного человека.
  const isForMe = user === myId;
  const roleOptions = roles.map((r) => {
    const isOthers = isBeyond(r._id);
    const isMine = !isOthers && isForMe && isBeyond(r._id, true);
    return {
      value: r._id,
      label: isOthers
        ? `${r.name} — ${t('roles.people.beyondMine')}`
        : isMine
          ? `${r.name} — ${t('roles.people.beyondSelf')}`
          : r.name,
      disabled: isOthers || isMine,
    };
  });

  // Одна строка на человека: владелец первым, дальше по имени.
  const byPerson = new Map<string, { name: string; chips: AssignmentRow[] }>();
  if (ownerId)
    byPerson.set(ownerId, {
      name: people.find((p) => p._id === ownerId)?.name ?? '—',
      chips: [],
    });
  for (const a of rows) {
    const entry = byPerson.get(a.user._id) ?? { name: a.user.name, chips: [] };
    entry.chips.push(a);
    byPerson.set(a.user._id, entry);
  }
  const persons = Array.from(byPerson.entries()).sort(([a, x], [b, y]) =>
    a === ownerId ? -1 : b === ownerId ? 1 : x.name.localeCompare(y.name),
  );

  /** Снимаю у себя последнюю роль, что даёт право раздавать роли, — экран закроется. */
  const isLastRolesGrant = (a: AssignmentRow) => {
    if (a.user._id !== myId) return false;
    if (perms?.isOwner) return false;
    const gives = (r: AssignmentRow) =>
      grantsOfRole(r.role._id).some((g) => g.permission === 'company.roles');
    return gives(a) && !rows.some((r) => r !== a && r.user._id === myId && gives(r));
  };

  const onRevoke = (a: AssignmentRow) =>
    modals.openConfirmModal({
      title: t('roles.people.revokeTitle'),
      centered: true,
      children: (
        <Text size="sm">
          {isLastRolesGrant(a)
            ? t('roles.people.revokeLastRoles', { role: a.role.name })
            : t('roles.people.revokeText', { name: a.user.name, role: a.role.name })}
        </Text>
      ),
      labels: { confirm: t('roles.people.revoke'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => revoke.mutate(a._id, { onError }),
    });

  const chipHint = (a: AssignmentRow) =>
    a.grantedBy
      ? t('roles.people.viaAssignment', {
          who: a.grantedBy.name,
          date: formatDateShort(a.grantedAt, locale),
        })
      : t('roles.people.viaAssignmentDate', { date: formatDateShort(a.grantedAt, locale) });

  return (
    <Stack gap="lg">
      <Paper withBorder radius="md" p={{ base: 'md', sm: 'lg' }}>
        <Group align="flex-end" gap="sm" wrap="wrap">
          <Select
            label={t('roles.people.person')}
            placeholder={t('roles.people.personPlaceholder')}
            data={people.map((p) => ({ value: p._id, label: p.name }))}
            value={user}
            onChange={setUser}
            searchable
            style={{ flex: '1 1 220px' }}
          />
          <Select
            label={t('roles.people.role')}
            placeholder={t('roles.people.rolePlaceholder')}
            data={roleOptions}
            value={role}
            onChange={setRole}
            style={{ flex: '1 1 220px' }}
          />
          <Button
            color="teal"
            disabled={!user || !role}
            loading={grant.isPending}
            onClick={() =>
              grant.mutate(
                { user: user!, role: role! },
                {
                  onSuccess: () => {
                    showToast({ title: t('roles.people.granted'), message: '', color: 'teal' });
                    setRole(null);
                  },
                  onError,
                },
              )
            }
          >
            {t('roles.people.grant')}
          </Button>
        </Group>
      </Paper>

      <Table verticalSpacing="sm">
        <Table.Thead>
          <Table.Tr>
            <Table.Th w="34%">{t('roles.people.person')}</Table.Th>
            <Table.Th>{t('roles.people.roles')}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {persons.map(([id, person]) => {
            const isOwnerRow = id === ownerId;
            return (
              <Table.Tr key={id} data-person={id}>
                <Table.Td style={{ verticalAlign: 'top' }}>
                  <Text size="sm" fw={500}>
                    {person.name}
                  </Text>
                  {isOwnerRow && (
                    <Text size="xs" c="dimmed">
                      {t('roles.people.ownerNote')}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Group gap={6}>
                    {isOwnerRow && (
                      <Badge
                        variant="light"
                        color="yellow"
                        size="lg"
                        radius="sm"
                        tt="none"
                        fw={500}
                      >
                        {t('roles.people.owner')}
                      </Badge>
                    )}
                    {person.chips.map((a) => {
                      const isRemovable = !isOwnerRow;
                      return (
                        <Tooltip key={a._id} label={chipHint(a)} withArrow openDelay={300}>
                          <Badge
                            size="lg"
                            radius="sm"
                            variant="light"
                            color="gray"
                            tt="none"
                            fw={500}
                            data-source={a.source}
                            rightSection={
                              isRemovable && (
                                <CloseButton
                                  size="xs"
                                  onClick={() => onRevoke(a)}
                                  aria-label={t('roles.people.revokeOf', {
                                    name: a.user.name,
                                    role: a.role.name,
                                  })}
                                />
                              )
                            }
                          >
                            {a.role.name}
                          </Badge>
                        </Tooltip>
                      );
                    })}
                  </Group>
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
      {!rows.length && (
        <Text size="sm" c="dimmed" ta="center">
          {t('roles.people.empty')}
        </Text>
      )}
    </Stack>
  );
};
